import React, { useState, useEffect } from 'react';
import {
  Modal,
  Steps,
  Button,
  Form,
  Input,
  Select,
  Space,
  Alert,
  Card,
  Tag,
  Descriptions,
  message,
  Spin,
  Typography,
  Collapse,
  List,
  Divider,
} from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { apiConfig, getActiveBrandId } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const { Step } = Steps;
const { Option } = Select;
const { Text } = Typography;
const { Panel } = Collapse;

interface AvailableField {
  fieldPath: string;
  fieldName: string;
  sampleValue: string;
  dataType: string;
  isRequired: boolean;
  frequency: number;
  filledCount: number;
  uniqueValues: number;
}

interface DatabaseColumn {
  name: string;
  label: string;
  type: string;
  required: boolean;
  description: string;
}

interface AnalysisData {
  analysis: {
    success: boolean;
    totalProductsAnalyzed: number;
    availableFields: AvailableField[];
    recommendations: string[];
    xmlUrl: string;
    analysisDate: string;
    brandId: string;
    brandName: string;
  };
  databaseColumns: DatabaseColumn[];
}

interface XmlMappingWizardProps {
  visible: boolean;
  brandId?: string; // Made optional, will use active brand if not provided
  onClose: () => void;
  onSuccess: () => void;
}

const XmlMappingWizard: React.FC<XmlMappingWizardProps> = ({
  visible,
  brandId: providedBrandId,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [form] = Form.useForm();
  const [fieldMappings, setFieldMappings] = useState<Record<string, string[]>>({});
  const [mappingName, setMappingName] = useState<string>('');

  // Get active brand ID based on environment (staging uses stageBrandId, prod uses brandId)
  const activeBrandId = providedBrandId || getActiveBrandId(user);

  useEffect(() => {
    if (visible && activeBrandId) {
      loadAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, activeBrandId]);

  const loadAnalysis = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${apiConfig.xmlMappingsApi}/api/v1/xml-mappings/analyze/${activeBrandId}`
      );
      const data = await response.json();

      if (data.success) {
        setAnalysisData(data.data);
        
        // Set mapping name to state
        const defaultName = `${data.data.analysis.brandName} Mapping`;
        setMappingName(defaultName);
        form.setFieldsValue({ mappingName: defaultName });
        
        // Auto-suggest mappings based on field names
        const autoMappings: Record<string, string[]> = {};
        data.data.databaseColumns.forEach((col: DatabaseColumn) => {
          const matchingFields = data.data.analysis.availableFields
            .filter((field: AvailableField) => 
              field.fieldName.toLowerCase().includes(col.name.toLowerCase()) ||
              col.name.toLowerCase().includes(field.fieldName.toLowerCase().replace('g:', ''))
            )
            .map((field: AvailableField) => field.fieldName);
          
          if (matchingFields.length > 0) {
            autoMappings[col.name] = matchingFields;
          }
        });
        
        setFieldMappings(autoMappings);
        message.success('XML analizi tamamlandı!');
      } else {
        message.error('XML analizi başarısız oldu');
      }
    } catch (error) {
      console.error('Failed to load analysis:', error);
      message.error('XML analizi yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldMappingChange = (columnName: string, selectedFields: string[]) => {
    setFieldMappings((prev) => ({
      ...prev,
      [columnName]: selectedFields,
    }));
  };

  const handleNext = () => {
    if (currentStep === 0) {
      form.validateFields(['mappingName']).then(() => {
        setCurrentStep(1);
      });
    } else if (currentStep === 1) {
      // Validate that required fields are mapped
      const requiredColumns = analysisData?.databaseColumns.filter((col) => col.required);
      const missingRequired = requiredColumns?.filter(
        (col) => !fieldMappings[col.name] || fieldMappings[col.name].length === 0
      );

      if (missingRequired && missingRequired.length > 0) {
        message.error(
          `Lütfen zorunlu alanları eşleştirin: ${missingRequired.map((c) => c.label).join(', ')}`
        );
        return;
      }

      setCurrentStep(2);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Get the latest mapping name from state
      console.log('🔍 Current mappingName state:', mappingName);
      
      if (!mappingName || !mappingName.trim()) {
        message.error('Lütfen bir mapping adı girin');
        setLoading(false);
        return;
      }

      const payload = {
        brandId: activeBrandId,
        mappingName: mappingName.trim(),
        fieldMappings,
        xpathConfig: {
          product_root: '//item',
          products_container: '//channel/item',
        },
        isActive: true,
      };

      console.log('📤 Creating mapping with payload:', payload);

      const response = await fetch(
        `${apiConfig.xmlMappingsApi}/api/v1/xml-mappings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      console.log('📥 API Response:', data);

      if (data.success) {
        message.success('Mapping başarıyla oluşturuldu!');
        onSuccess();
        handleClose();
      } else {
        const errorMsg = data.message || data.error || 'Mapping oluşturulamadı';
        console.error('❌ API Error:', data);
        message.error(`Hata: ${errorMsg}`);
      }
    } catch (error: any) {
      console.error('❌ Failed to create mapping:', error);
      const errorMsg = error.message || 'Mapping oluşturulurken hata oluştu';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setFieldMappings({});
    setMappingName('');
    form.resetFields();
    setAnalysisData(null);
    onClose();
  };

  const renderStep0 = () => {
    if (!analysisData) return null;

    return (
      <div>
        <Alert
          message="XML Analiz Sonuçları"
          description={`${analysisData.analysis.totalProductsAnalyzed} ürün analiz edildi, ${analysisData.analysis.availableFields.length} field tespit edildi.`}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={form} layout="vertical">
          <Form.Item
            name="mappingName"
            label="Mapping Adı"
            rules={[{ required: true, message: 'Lütfen bir mapping adı girin' }]}
          >
            <Input 
              placeholder="Örn: default_v1 kiğılı mapping"
              value={mappingName}
              onChange={(e) => setMappingName(e.target.value)}
            />
          </Form.Item>
        </Form>

        <Card title="Analiz Özeti" size="small" style={{ marginTop: 16 }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Marka">
              <Text strong>{analysisData.analysis.brandName}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="XML URL">
              <a href={analysisData.analysis.xmlUrl} target="_blank" rel="noopener noreferrer">
                {analysisData.analysis.xmlUrl}
              </a>
            </Descriptions.Item>
            <Descriptions.Item label="Analiz Tarihi">
              {new Date(analysisData.analysis.analysisDate).toLocaleString('tr-TR')}
            </Descriptions.Item>
            <Descriptions.Item label="Toplam Ürün">
              <Tag color="blue">{analysisData.analysis.totalProductsAnalyzed}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Tespit Edilen Field">
              <Tag color="green">{analysisData.analysis.availableFields.length}</Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {analysisData?.analysis?.recommendations?.length > 0 && (
          <Card title="Öneriler" size="small" style={{ marginTop: 16 }}>
            <List
              size="small"
              dataSource={analysisData?.analysis?.recommendations}
              renderItem={(item) => (
                <List.Item>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                  {item}
                </List.Item>
              )}
            />
          </Card>
        )}

        <Collapse style={{ marginTop: 16 }}>
          <Panel header="Tüm XML Field'ları Görüntüle" key="1">
            <div style={{ maxHeight: 300, overflow: 'auto' }}>
              {analysisData.analysis.availableFields.map((field, index) => (
                <div key={index} style={{ marginBottom: 8, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text strong>{field.fieldName}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Sample: {field.sampleValue || '(boş)'}
                    </Text>
                    <Space size="small">
                      <Tag color="blue">{field.dataType}</Tag>
                      <Tag>Filled: {field.filledCount}/{field.frequency}</Tag>
                      <Tag>Unique: {field.uniqueValues}</Tag>
                    </Space>
                  </Space>
                </div>
              ))}
            </div>
          </Panel>
        </Collapse>
      </div>
    );
  };

  const renderStep1 = () => {
    if (!analysisData) return null;

    return (
      <div>
        <Alert
          message="Field Eşleştirme"
          description="Database kolonları ile XML field'larını eşleştirin. Bir kolona birden fazla field atayabilirsiniz."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ maxHeight: 500, overflow: 'auto', padding: '0 8px' }}>
          {analysisData.databaseColumns.map((column) => {
            const selectedFields = fieldMappings[column.name] || [];
            const hasValue = selectedFields.length > 0;

            return (
              <Card
                key={column.name}
                size="small"
                style={{
                  marginBottom: 12,
                  border: column.required && !hasValue ? '1px solid #ff4d4f' : undefined,
                }}
                title={
                  <Space>
                    <Text strong>{column.label}</Text>
                    {column.required && (
                      <Tag color="red" icon={<WarningOutlined />}>
                        Gerekli
                      </Tag>
                    )}
                  </Space>
                }
                extra={<Tag color="purple">{column.type}</Tag>}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {column.description}
                  </Text>

                  <Select
                    mode="multiple"
                    placeholder="XML field'larını seçin veya manuel yazın (örn: g:custom_field)..."
                    style={{ width: '100%' }}
                    value={selectedFields}
                    onChange={(values) => handleFieldMappingChange(column.name, values)}
                    showSearch
                    filterOption={(input, option) => {
                      if (!option?.children) return false;
                      const childrenStr = String(option.children);
                      return childrenStr.toLowerCase().includes(input.toLowerCase());
                    }}
                  >
                    {analysisData.analysis.availableFields.map((field) => (
                      <Option key={field.fieldName} value={field.fieldName}>
                        {field.fieldName}
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                          ({field.filledCount}/{field.frequency} dolu)
                        </Text>
                      </Option>
                    ))}
                  </Select>

                  {selectedFields.length > 0 && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>Seçili fieldlar:</Text>
                      <div style={{ marginTop: 4 }}>
                        {selectedFields.map((field) => {
                          const fieldData = analysisData.analysis.availableFields.find(
                            (f) => f.fieldName === field
                          );
                          return (
                            <Tag key={field} color="blue" style={{ marginTop: 4 }}>
                              {field}
                              {fieldData && (
                                <Text style={{ fontSize: 10, marginLeft: 4 }}>
                                  ({fieldData.filledCount}/{fieldData.frequency})
                                </Text>
                              )}
                            </Tag>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Space>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  const renderStep2 = () => {
    if (!analysisData) return null;

    const mappedCount = Object.values(fieldMappings).filter((v) => v.length > 0).length;
    const totalColumns = analysisData.databaseColumns.length;
    const requiredColumns = analysisData.databaseColumns.filter((col) => col.required);
    const mappedRequired = requiredColumns.filter(
      (col) => fieldMappings[col.name] && fieldMappings[col.name].length > 0
    ).length;

    return (
      <div>
        <Alert
          message="Mapping Özeti"
          description="Mapping bilgilerinizi kontrol edin ve kaydedin."
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Card title="Genel Bilgiler" size="small" style={{ marginBottom: 16 }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Mapping Adı">
              <Text strong>{mappingName}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Toplam Eşleştirme">
              <Tag color="blue">
                {mappedCount}/{totalColumns} kolon
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Zorunlu Alanlar">
              <Tag color={mappedRequired === requiredColumns.length ? 'green' : 'red'}>
                {mappedRequired}/{requiredColumns.length} tamamlandı
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Field Eşleştirmeleri" size="small">
          <div style={{ maxHeight: 400, overflow: 'auto' }}>
            {Object.entries(fieldMappings)
              .filter(([_, fields]) => fields.length > 0)
              .map(([columnName, fields]) => {
                const column = analysisData.databaseColumns.find((c) => c.name === columnName);
                return (
                  <div
                    key={columnName}
                    style={{
                      marginBottom: 12,
                      padding: 12,
                      background: '#f5f5f5',
                      borderRadius: 4,
                    }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      <div>
                        <Text strong>{column?.label}</Text>
                        {column?.required && <Tag color="red" style={{ marginLeft: 8 }}>Gerekli</Tag>}
                      </div>
                      <Space wrap>
                        {fields.map((field) => (
                          <Tag key={field} color="blue">
                            {field}
                          </Tag>
                        ))}
                      </Space>
                    </Space>
                  </div>
                );
              })}
          </div>
        </Card>
      </div>
    );
  };

  const steps = [
    {
      title: 'Analiz',
      content: renderStep0(),
    },
    {
      title: 'Field Eşleştirme',
      content: renderStep1(),
    },
    {
      title: 'Özet',
      content: renderStep2(),
    },
  ];

  return (
    <Modal
      title="Yeni XML Mapping Oluştur"
      open={visible}
      onCancel={handleClose}
      width={900}
      footer={null}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Steps current={currentStep} style={{ marginBottom: 24 }}>
          {steps.map((item) => (
            <Step key={item.title} title={item.title} />
          ))}
        </Steps>

        <div style={{ minHeight: 400 }}>{steps[currentStep].content}</div>

        <Divider />

        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={handleClose}>İptal</Button>
            {currentStep > 0 && (
              <Button onClick={handlePrev}>Geri</Button>
            )}
            {currentStep < steps.length - 1 && (
              <Button type="primary" onClick={handleNext}>
                İleri
              </Button>
            )}
            {currentStep === steps.length - 1 && (
              <Button type="primary" onClick={handleSubmit} loading={loading}>
                Kaydet
              </Button>
            )}
          </Space>
        </div>
      </Spin>
    </Modal>
  );
};

export default XmlMappingWizard;

